/**
 * Pure unit-economics engine — Plaid COGS, profile P&L, growth projection, actuals compare.
 * V1: assumptions only; compareModeledVsActual accepts fixture ActualsSnapshot shapes.
 */

import { PLAID_GROWTH_PLATFORM_FEE } from './seedDefaults';

function roundMoney(n) {
  return Math.round(n * 100) / 100;
}

/**
 * @param {number} items
 * @param {number} balanceWaves
 * @param {Record<string, unknown>} addOns
 * @param {Record<string, number>} rates
 */
export function plaidCogs(items, balanceWaves, addOns = {}, rates) {
  const r = rates;
  const rate = (key) => Math.max(0, Number(r?.[key]) || 0);
  const itemCount = Math.max(0, Number(items) || 0);
  const waves = Math.max(0, Number(balanceWaves) || 0);
  const txRefreshCallsMo = Math.max(0, Number(addOns.txRefreshCallsMo) || 0);
  let c =
    itemCount * rate('transactionsItemMo') +
    itemCount * waves * rate('balanceCall') +
    txRefreshCallsMo * rate('txRefreshCall');
  if (addOns.recurring) c += itemCount * rate('recurringItemMo');
  if (addOns.liabilities) c += itemCount * rate('liabilitiesItemMo');
  if (addOns.investments) {
    c += itemCount * (rate('investmentsHoldingsItemMo') + rate('investmentsTxItemMo'));
  }
  if (addOns.enrichThousandTx) {
    c += (Math.max(0, Number(addOns.enrichThousandTx) || 0) / 1000) * rate('enrichPerThousand');
  }
  return roundMoney(c);
}

/**
 * Optional one-time Auth cost. PCC-8 drops Auth from the default Link
 * products; profiles must opt in explicitly when modeling a future ACH flow.
 */
export function authOnceCogs(profile, rates = {}, plan = {}) {
  if (!profile?.authEnabled || plan.bankLink === false) return 0;
  return roundMoney(
    Math.max(0, Number(profile.items) || 0) * Math.max(0, Number(rates.authOnce) || 0)
  );
}

/**
 * Effective portfolio Plaid COGS for a month given billing mode.
 * @param {number} paygCogs — modeled PAYG total
 * @param {'payg'|'growth_platform'} plaidMode
 * @param {number} [platformFee]
 */
export function plaidCogsEffective(
  paygCogs,
  plaidMode = 'payg',
  platformFee = PLAID_GROWTH_PLATFORM_FEE
) {
  if (plaidMode === 'growth_platform') return platformFee;
  return paygCogs;
}

/**
 * First month where modeled PAYG COGS meets or exceeds the Growth platform fee.
 * @param {object[]} months — projection rows with plaidCogsPayg or plaidCogs
 * @param {number} [platformFee]
 * @returns {{ monthIndex: number, label: string }|null}
 */
export function findPaygPlatformCrossoverMonth(months, platformFee = PLAID_GROWTH_PLATFORM_FEE) {
  if (!months?.length) return null;
  for (const month of months) {
    const payg = month.plaidCogsPayg ?? month.plaidCogsP50 ?? month.plaidCogs;
    if (payg != null && payg >= platformFee) {
      return { monthIndex: month.monthIndex, label: month.label };
    }
  }
  return null;
}

/**
 * @param {number} revenue — dollars
 * @param {Record<string, number>} [rates]
 */
export function stripeFee(revenue, rates = {}) {
  const amount = Math.max(0, Number(revenue) || 0);
  if (amount <= 0) return 0;
  const pct = Math.max(0, Number(rates.stripePercent ?? 0.029) || 0);
  const fixed = Math.max(0, Number(rates.stripeFixed ?? 0.3) || 0);
  return roundMoney(amount * pct + fixed);
}

/**
 * AI chat COGS for consumed turns (Premium+ only in product; Basic/Free = 0).
 * @param {number} turnsMo
 * @param {Record<string, number>} rates
 */
export function aiCogs(turnsMo, rates = {}) {
  const turns = Math.max(0, Number(turnsMo) || 0);
  const perTurn = Math.max(0, Number(rates.aiCostPerTurn ?? 0.04) || 0);
  return roundMoney(turns * perTurn);
}

/**
 * Included quota + overage packs (CI-minutes style). Free/Basic ineligible.
 * Power users buy packs to cover turns above included; packs priced above vendor cost.
 * @param {object} profile
 * @param {Record<string, number>} rates
 */
export function resolveAiUsage(profile, rates = {}) {
  const empty = {
    eligible: false,
    turnsMo: 0,
    includedTurns: 0,
    overageTurns: 0,
    packs: 0,
    packRevenue: 0,
    aiCogs: 0,
  };
  const plan = profile?.plan;
  const eligible = plan === 'premium' || plan === 'enterprise';
  if (!eligible) return empty;

  const turnsMo = Math.max(0, profile.aiTurnsMo || 0);
  const includedTurns =
    plan === 'enterprise'
      ? rates.aiIncludedTurnsEnterprise ?? rates.aiIncludedTurnsPremium ?? 40
      : rates.aiIncludedTurnsPremium ?? 40;
  const packTurns = Math.max(1, rates.aiPackTurns ?? 100);
  const packPrice = Math.max(0, rates.aiPackPrice ?? 8);
  const overageTurns = Math.max(0, turnsMo - includedTurns);
  const packs = overageTurns > 0 ? Math.ceil(overageTurns / packTurns) : 0;
  const packRevenue = roundMoney(packs * packPrice);
  const cogs = aiCogs(turnsMo, rates);

  return {
    eligible: true,
    turnsMo,
    includedTurns,
    overageTurns,
    packs,
    packRevenue,
    aiCogs: cogs,
  };
}

/**
 * @param {number} planMonthly
 * @param {number} balanceWaves
 * @param {Record<string, number>} rates
 */
export function breakEvenItems(planMonthly, balanceWaves, rates) {
  const perItem =
    Math.max(0, Number(rates?.transactionsItemMo) || 0) +
    Math.max(0, Number(balanceWaves) || 0) * Math.max(0, Number(rates?.balanceCall) || 0);
  if (perItem <= 0) return Infinity;
  return Math.round((planMonthly / perItem) * 10) / 10;
}

/**
 * @param {object} profile
 * @param {Record<string, { monthly: number }>} plans
 * @param {Record<string, number>} rates
 */
export function profilePnL(profile, plans, rates) {
  const plan = plans[profile.plan] || { monthly: 0 };
  const planRevenue = Math.max(0, Number(plan.monthly) || 0);
  const linkedItems = plan.bankLink === false ? 0 : Math.max(0, Number(profile.items) || 0);
  const aiUsage = resolveAiUsage(profile, rates);
  const aiPackRevenue = aiUsage.packRevenue;
  const revenue = roundMoney(planRevenue + aiPackRevenue);
  const cogs = plaidCogs(linkedItems, profile.balanceWaves, profile.addOns || {}, rates);
  const authCogs = authOnceCogs({ ...profile, items: linkedItems }, rates, plan);
  const stripe = stripeFee(revenue, rates);
  const ai = aiUsage.aiCogs;
  const gmPlaid = roundMoney(planRevenue - cogs);
  const gmNet = roundMoney(revenue - cogs - stripe - ai);
  const gmPct = planRevenue > 0 ? gmPlaid / planRevenue : NaN;
  const gmNetPct = revenue > 0 ? gmNet / revenue : NaN;
  const be = breakEvenItems(planRevenue, profile.balanceWaves, rates);

  let status = 'healthy';
  if (planRevenue <= 0) status = 'not_monetized';
  else if (gmNet < 0) status = 'danger';
  else if (gmNetPct < 0.35 || gmNet < 3) status = 'thin';
  else if (gmNetPct < 0.55) status = 'ok';

  return {
    revenue,
    planRevenue,
    aiPackRevenue,
    authCogs,
    aiPacks: aiUsage.packs,
    aiOverageTurns: aiUsage.overageTurns,
    aiIncludedTurns: aiUsage.includedTurns,
    linkedItems,
    cogs,
    stripe,
    ai,
    gmPlaid,
    gmNet,
    gmPct,
    gmNetPct,
    be,
    status,
  };
}

/**
 * Estimate Plaid COGS from bank-link aggregates using the same rate card.
 * @param {object|null} bankLink
 * @param {Record<string, number>} rates
 * @param {number} defaultWavesPerItem
 */
export function estimateActualPlaidCogs(bankLink, rates, defaultWavesPerItem = 8) {
  if (!bankLink) return null;
  const items = bankLink.activeItems || 0;
  const waves =
    bankLink.estimatedBalanceWavesMo != null
      ? bankLink.estimatedBalanceWavesMo / Math.max(items, 1)
      : defaultWavesPerItem;
  return plaidCogs(items, waves, {}, rates);
}

/**
 * @param {object} modeledMonth — row from projectGrowth
 * @param {object|null} actuals — ActualsSnapshot shape
 * @param {Record<string, number>} rates
 */
export function compareModeledVsActual(modeledMonth, actuals, rates) {
  if (!actuals) {
    return {
      revenueDelta: null,
      mrrDeltaPct: null,
      estimatedActualPlaidCogs: null,
      cogsDelta: null,
      marginDelta: null,
      mixHint: null,
    };
  }

  // Admin MRR is recurring subscription revenue; do not compare it with
  // one-time AI pack revenue included in the total modeled revenue field.
  const modeledRevenue = modeledMonth?.planRevenue ?? modeledMonth?.revenue ?? 0;
  const modeledCogs = modeledMonth?.plaidCogs ?? 0;
  // The actuals overlay only has MRR and estimated Plaid COGS. Compare the
  // same Plaid-only gross margin rather than the forecast contribution margin,
  // which also includes AI and one-time Auth costs.
  const modeledMargin = modeledRevenue - modeledCogs;

  const hasActualMrr = actuals.mrrCents != null && Number.isFinite(Number(actuals.mrrCents));
  const actualMrr = hasActualMrr ? Number(actuals.mrrCents) / 100 : null;
  const revenueDelta = hasActualMrr ? roundMoney(actualMrr - modeledRevenue) : null;
  const mrrDeltaPct =
    modeledRevenue > 0 && revenueDelta != null
      ? roundMoney((revenueDelta / modeledRevenue) * 100)
      : null;

  const estimatedActualPlaidCogs = estimateActualPlaidCogs(actuals.bankLink, rates);
  const cogsDelta =
    estimatedActualPlaidCogs != null ? roundMoney(estimatedActualPlaidCogs - modeledCogs) : null;
  const actualMargin =
    hasActualMrr && estimatedActualPlaidCogs != null
      ? roundMoney(actualMrr - estimatedActualPlaidCogs)
      : null;
  const marginDelta =
    actualMargin != null ? roundMoney(actualMargin - modeledMargin) : null;

  let mixHint = null;
  if (actuals.planBreakdown?.length && modeledMonth?.mix?.length) {
    const actualTotal = actuals.planBreakdown.reduce((s, row) => s + (row.count || 0), 0);
    const actualShares = Object.fromEntries(
      actuals.planBreakdown.map((row) => [
        row.tierSlug,
        actualTotal > 0 ? (row.count || 0) / actualTotal : 0,
      ])
    );
    const modeledPlanShares = {};
    modeledMonth.mix.forEach(({ profileId, plan, share }) => {
      const tier =
        plan ||
        (profileId.includes('enterprise')
          ? 'enterprise'
          : profileId.includes('premium')
            ? 'premium'
            : profileId.includes('free')
              ? 'free'
              : 'basic');
      modeledPlanShares[tier] = (modeledPlanShares[tier] || 0) + share;
    });
    const tiers = new Set([...Object.keys(actualShares), ...Object.keys(modeledPlanShares)]);
    let distance = 0;
    tiers.forEach((tier) => {
      distance += Math.abs((actualShares[tier] || 0) - (modeledPlanShares[tier] || 0));
    });
    mixHint =
      distance > 0.25
        ? 'Actual tier mix differs from scenario — recalibrate profile shares'
        : 'Tier mix roughly aligned with scenario';
  }

  return {
    revenueDelta,
    mrrDeltaPct,
    estimatedActualPlaidCogs,
    cogsDelta,
    marginDelta,
    mixHint,
  };
}

export function formatMoney(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toFixed(2)}`;
}

export function formatPct(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(0)}%`;
}
